import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
// @ts-ignore - Ignore TypeScript errors for lucide-react
import { BookOpen, Clock, Search, Star, PlayCircle, Loader } from "lucide-react";
import { formationAPI } from "../../services/api";

// Define interface for Formation object (matching your DB schema)
interface Formation {
  id: number;
  name: string;
  lastUpdate: string;
  totalDuration: number;
  categoryId: number;
  shortDescription: string;
  longDescription: string;
  videoDescription: string;
  image: string | null;
  gratuit: boolean;
  price: number | null;
  currency: string;
  createdAt: string;
  updatedAt: string;
  chapitreCount: number;
  categoryName: string | null;
  FormationCategory?: {
    name: string;
  };
  formateur?: {
    id: number;
    name: string | null;
    photo: string | null;
  } | null;
}

export default function CoursesPage() {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>("newest");
  const [formations, setFormations] = useState<Formation[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch formations from API
  useEffect(() => {
    const fetchFormations = async () => {
      try {
        setLoading(true);
        // Follow the same pattern as HomePage - directly access response.data
        const response = await formationAPI.getAll();
        const formationsData = response.data; // Extract data from Axios response

        setFormations(formationsData);

        // Extract unique categories
        const uniqueCategories = Array.from(
          new Set(
            formationsData
              .map((f: Formation) => f.categoryName || f.FormationCategory?.name)
              .filter(Boolean)
          )
        ) as string[];
        setCategories(uniqueCategories);

      } catch (err: any) {
        console.error('Error fetching formations:', err);

        // More detailed error handling like in HomePage
        if (err.response?.status === 404) {
          setError('API endpoint not found. Please check backend server.');
        } else if (err.code === 'ECONNREFUSED') {
          setError('Cannot connect to server. Please check if backend is running.');
        } else {
          setError(`Error loading formations: ${err.message}`);
        }

        // Fallback to empty array
        setFormations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFormations();
  }, []);

  // Helper function to format duration (same as HomePage)
  const formatDuration = (totalMinutes: number): string => {
    if (!totalMinutes || totalMinutes === 0) return "0 min"

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (hours === 0) {
      return `${minutes} min`
    } else if (minutes === 0) {
      return hours === 1 ? `${hours} heure` : `${hours} heures`
    } else {
      const hourText = hours === 1 ? "heure" : "heures"
      return `${hours} ${hourText} ${minutes} min`
    }
  };

  // Helper function to format price (improved like HomePage)
  const formatPrice = (formation: Formation): string => {
    if (formation.gratuit === true) {
      return 'Gratuit';
    }

    if (formation.price && formation.price > 0) {
      const price = parseFloat(formation.price.toString());
      return `${price} ${formation.currency || 'MAD'}`;
    }

    return 'Gratuit';
  };

  // Helper function to get category name
  const getCategoryName = (formation: Formation): string => {
    return formation.categoryName || formation.FormationCategory?.name || 'Autres';
  };

  const handleCategoryChange = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter((c) => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const filteredFormations = formations.filter((formation) => {
    // Filter by search term
    const matchesSearch =
      formation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (formation.shortDescription && formation.shortDescription.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (formation.longDescription && formation.longDescription.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filter by category
    const formationCategory = getCategoryName(formation);
    const matchesCategory = selectedCategories.length === 0 ||
      selectedCategories.includes(formationCategory);

    return matchesSearch && matchesCategory;
  });

  // Sort formations
  const sortedFormations = [...filteredFormations].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.createdAt || b.lastUpdate).getTime() -
             new Date(a.createdAt || a.lastUpdate).getTime();
    }
    if (sortBy === "duration-low") return (a.totalDuration || 0) - (b.totalDuration || 0);
    if (sortBy === "duration-high") return (b.totalDuration || 0) - (a.totalDuration || 0);
    if (sortBy === "alphabetical") return a.name.localeCompare(b.name);
    return 0;
  });

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="container px-4 py-8 md:px-6 md:py-12">
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin mr-2" />
            <span>Chargement des formations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="container px-4 py-8 md:px-6 md:py-12">
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-xl text-red-500 mb-4">
              Erreur lors du chargement des formations: {error}
            </p>
            <Button onClick={() => window.location.reload()}>
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <h1 className="text-3xl font-serif font-bold mb-8">Nos formations</h1>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Filters Sidebar */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-serif font-medium mb-4">Rechercher</h3>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400" />
                <Input
                  type="search"
                  placeholder="Rechercher une formation..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Categories Filter */}
            {categories.length > 0 && (
              <div>
                <h3 className="text-lg font-serif font-medium mb-4">Thématiques</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div key={category} className="flex items-center space-x-2">
                      <Checkbox
                        id={category}
                        checked={selectedCategories.includes(category)}
                        onCheckedChange={() => handleCategoryChange(category)}
                      />
                      <label
                        htmlFor={category}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-serif font-medium mb-4">Trier par</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="newest"
                    name="sortBy"
                    value="newest"
                    checked={sortBy === "newest"}
                    onChange={() => setSortBy("newest")}
                    className="h-4 w-4"
                  />
                  <label htmlFor="newest" className="text-sm font-medium">
                    Plus récent
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="alphabetical"
                    name="sortBy"
                    value="alphabetical"
                    checked={sortBy === "alphabetical"}
                    onChange={() => setSortBy("alphabetical")}
                    className="h-4 w-4"
                  />
                  <label htmlFor="alphabetical" className="text-sm font-medium">
                    Alphabétique
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="duration-low"
                    name="sortBy"
                    value="duration-low"
                    checked={sortBy === "duration-low"}
                    onChange={() => setSortBy("duration-low")}
                    className="h-4 w-4"
                  />
                  <label htmlFor="duration-low" className="text-sm font-medium">
                    Durée (croissante)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="duration-high"
                    name="sortBy"
                    value="duration-high"
                    checked={sortBy === "duration-high"}
                    onChange={() => setSortBy("duration-high")}
                    className="h-4 w-4"
                  />
                  <label htmlFor="duration-high" className="text-sm font-medium">
                    Durée (décroissante)
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Formations Grid */}
          <div className="lg:col-span-3">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500 dark:text-gray-400">
                {sortedFormations.length} formation{sortedFormations.length !== 1 ? 's' : ''} trouvée{sortedFormations.length !== 1 ? 's' : ''}
              </p>
            </div>

            {sortedFormations.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {sortedFormations.map((formation) => (
                  <a
                    href={`/courses/${formation.id}`}
                    key={formation.id}
                    className="transition-transform hover:scale-105"
                  >
                    <Card className="h-full overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                      <CardHeader className="p-0">
                        <div className="relative h-48 w-full group">
                          <img
                            src={formation.image || "/placeholder.svg"}
                            alt={formation.name}
                            className="object-cover h-full w-full"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "/placeholder.svg";
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <PlayCircle className="w-16 h-16 text-white opacity-80" />
                          </div>
                          <div className="absolute top-0 right-0 m-2">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 text-blue-600">
                              {getCategoryName(formation)}
                            </span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <img
                              src={formation.formateur?.photo || "/avatar.png"}
                              alt={formation.formateur?.name || "Elille academy"}
                              width={32}
                              height={32}
                              className="rounded-full mr-2 border-2 border-white shadow-sm"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "/placeholder.svg";
                              }}
                            />
                            <span className="text-sm font-medium">
                              {formation.formateur?.name || "Elille academy"}
                            </span>
                          </div>
                        </div>
                        <CardTitle className="text-xl mb-3 line-clamp-2 h-14">
                          {formation.name}
                        </CardTitle>
                        <div className="flex flex-wrap gap-3 mt-4">
                          <div className="flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
                            <Clock className="w-4 h-4 mr-1.5 text-blue-600" />
                            <span className="text-sm font-medium">
                              {formatDuration(formation.totalDuration)}
                            </span>
                          </div>
                          <div className="flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
                            <BookOpen className="w-4 h-4 mr-1.5 text-red-600" />
                            <span className="text-sm font-medium">
                              {formation.chapitreCount || 0} module{(formation.chapitreCount || 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                      <div className="px-5 pb-5 pt-0 flex justify-between items-center border-t border-gray-100 dark:border-gray-800 mt-2">
                        <span className="text-lg font-bold text-green-600">
                          {formatPrice(formation)}
                        </span>
                        <Button size="sm" className="bg-[#00088E] hover:bg-blue-700">
                          Voir la formation
                        </Button>
                      </div>
                    </Card>
                  </a>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-xl text-gray-500 dark:text-gray-400 mb-4">
                  Aucune formation ne correspond à votre recherche
                </p>
                <Button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategories([]);
                  }}
                >
                  Réinitialiser les filtres
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
